import { isNativePlatform } from './device';
import { saveBlobToDownloads } from './nativeBlobDownload';

const REVOKE_DELAY_MS = 200;

export async function parseBlobError(blob) {
  if (!(blob instanceof Blob)) {
    return null;
  }
  const type = (blob.type || '').toLowerCase();
  if (!type.includes('json') && blob.size > 0) {
    const head = await blob.slice(0, 1).text();
    if (head !== '{') {
      return null;
    }
  }
  try {
    const text = await blob.text();
    const data = JSON.parse(text);
    return data?.message || data?.error || null;
  } catch {
    return null;
  }
}

export async function assertBlobKind(blob, kind) {
  if (!(blob instanceof Blob) || blob.size === 0) {
    throw new Error(`Invalid ${kind} file (empty response)`);
  }
  const head = await blob.slice(0, 4).text();
  if (kind === 'pdf' && !head.startsWith('%PDF')) {
    const msg = await parseBlobError(blob);
    throw new Error(msg || 'Server did not return a valid PDF');
  }
  if (kind === 'zip' && !head.startsWith('PK')) {
    const msg = await parseBlobError(blob);
    throw new Error(msg || 'Server did not return a valid ZIP archive');
  }
}

export async function downloadBlob(blob, filename) {
  if (isNativePlatform()) {
    const result = await saveBlobToDownloads(blob, filename);
    return {
      saved: true,
      filename: result?.filename || filename,
      uri: result?.uri,
    };
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
  return { saved: true, filename };
}

export function filenameFromDisposition(disposition, fallback) {
  const match = (disposition || '').match(/filename="([^"]+)"/);
  return match?.[1] || fallback;
}

export async function handleBlobDownloadResponse(response, kind, fallbackName) {
  const blob = response.data;
  const msg = await parseBlobError(blob);
  if (msg) {
    throw new Error(msg);
  }
  await assertBlobKind(blob, kind);
  const filename = filenameFromDisposition(
    response.headers['content-disposition'],
    fallbackName,
  );
  await downloadBlob(blob, filename);
  return { filename, blob };
}

export async function openPdfBlob(blob, filename = 'document.pdf') {
  if (isNativePlatform()) {
    await downloadBlob(blob, filename);
    return { saved: true, native: true };
  }

  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
  return { saved: false, native: false };
}

export async function getApiErrorMessage(error) {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    const msg = await parseBlobError(data);
    if (msg) return msg;
  }
  if (data && typeof data === 'object' && data.message) {
    return data.message;
  }
  return error?.message || 'Request failed';
}
